<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>首页轮播图</span>
        <div class="header-actions">
          <el-button :loading="loading" @click="loadData">刷新</el-button>
          <el-button type="primary" @click="openCreate">新增轮播图</el-button>
        </div>
      </div>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="轮播图加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template #default>
          <span>可以稍后重试，或先检查后端服务状态。</span>
          <el-button type="text" @click="reloadAll">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-table v-if="loading || list.length" v-loading="loading" :data="list" border>
          <el-table-column label="图片" width="150">
            <template #default="{ row }">
              <div class="banner-thumb">
                <img v-if="row.imageUrl" :src="row.imageUrl" alt="轮播图">
                <div v-else class="banner-thumb__empty">暂无图片</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="文案" min-width="260">
            <template #default="{ row }">
              <div class="banner-title">{{ row.title }}</div>
              <div class="sub-text">{{ row.subTitle || '未填写副标题' }}</div>
            </template>
          </el-table-column>
          <el-table-column label="跳转信息" min-width="220">
            <template #default="{ row }">
              <div>{{ jumpTypeText(row.jumpType) }}</div>
              <div class="sub-text">{{ jumpLabel(row) }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="sort" label="排序" width="90" />
          <el-table-column label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="Number(row.status) === 1 ? 'success' : 'info'">
                {{ Number(row.status) === 1 ? '启用中' : '已停用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="280">
            <template #default="{ row }">
              <el-button type="text" @click="openEdit(row.id)">编辑</el-button>
              <el-button
                v-if="Number(row.status) === 1"
                type="text"
                :loading="actionId === row.id"
                @click="down(row)"
              >停用</el-button>
              <el-button
                v-else
                type="text"
                :loading="actionId === row.id"
                @click="up(row)"
              >启用</el-button>
              <el-button type="text" :loading="actionId === row.id" @click="remove(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-empty v-else description="当前还没有轮播图" />
      </template>
    </el-card>

    <el-dialog
      :title="form.id ? '编辑轮播图' : '新增轮播图'"
      :visible.sync="dialogVisible"
      width="760px"
      @closed="resetForm"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="主标题" prop="title">
          <el-input v-model.trim="form.title" placeholder="例如：春茶上市，尝鲜正当时" />
        </el-form-item>
        <el-form-item label="副标题" prop="subTitle">
          <el-input
            v-model.trim="form.subTitle"
            placeholder="例如：寻见高山云雾中的一抹清香"
          />
        </el-form-item>
        <el-form-item label="轮播图" prop="imageUrl">
          <div class="upload-row">
            <div class="banner-thumb banner-thumb--large">
              <img v-if="form.imageUrl" :src="form.imageUrl" alt="轮播图预览">
              <div v-else class="banner-thumb__empty">上传后预览</div>
            </div>
            <div class="upload-panel">
              <el-input v-model.trim="form.imageUrl" placeholder="请输入图片地址或直接上传" />
              <el-upload
                class="upload-inline"
                action="#"
                :show-file-list="false"
                :http-request="uploadImage"
              >
                <el-button type="text" :loading="uploading">上传图片</el-button>
              </el-upload>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="跳转类型" prop="jumpType">
          <el-select v-model="form.jumpType" placeholder="请选择跳转类型" @change="handleJumpTypeChange">
            <el-option :value="1" label="商品详情" />
            <el-option :value="2" label="分类页" />
            <el-option :value="3" label="不跳转" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="Number(form.jumpType) === 1" label="跳转商品" prop="jumpValue">
          <el-select
            v-model="form.jumpValue"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="请选择商品"
            :remote-method="searchProducts"
            :loading="productLoading"
          >
            <el-option
              v-for="item in productOptions"
              :key="item.id"
              :label="item.name"
              :value="String(item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="Number(form.jumpType) === 2" label="跳转分类" prop="jumpValue">
          <el-select
            v-model="form.jumpValue"
            filterable
            clearable
            placeholder="请选择分类"
          >
            <el-option
              v-for="item in categoryOptions"
              :key="item.id"
              :label="item.name"
              :value="String(item.id)"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="Number(form.jumpType) === 3" label="跳转值">
          <el-input value="当前不需要填写" disabled />
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="form.sort" :min="0" :max="9999" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  addBanner,
  deleteBanner,
  downBanner,
  getBannerDetail,
  getBannerList,
  getCategoryList,
  getProductDetail,
  getProductPage,
  upBanner,
  updateBanner,
  uploadAdminImage
} from '../../api/admin'

const createForm = () => ({
  id: null,
  title: '',
  subTitle: '',
  imageUrl: '',
  jumpType: 3,
  jumpValue: '',
  sort: 1,
  status: 1
})

export default {
  data() {
    return {
      list: [],
      loading: false,
      loadError: false,
      dialogVisible: false,
      form: createForm(),
      saving: false,
      uploading: false,
      actionId: null,
      productLoading: false,
      productOptions: [],
      productCache: {},
      categoryOptions: [],
      rules: {
        title: [{ required: true, message: '请输入轮播图标题', trigger: 'blur' }],
        imageUrl: [{ required: true, message: '请上传或填写轮播图片', trigger: 'blur' }],
        jumpType: [{ required: true, message: '请选择跳转类型', trigger: 'change' }],
        jumpValue: [{ required: true, message: '请选择跳转目标', trigger: 'change' }],
        sort: [{ required: true, message: '请输入排序值', trigger: 'change' }]
      }
    }
  },
  created() {
    this.reloadAll()
  },
  methods: {
    jumpTypeText(type) {
      const map = { 1: '商品详情', 2: '分类页', 3: '不跳转' }
      return map[type] || '未知类型'
    },
    jumpLabel(row) {
      if (Number(row.jumpType) === 1) {
        const product = this.productOptions.find(item => String(item.id) === String(row.jumpValue))
        return product ? `${product.name}（ID: ${row.jumpValue}）` : `商品ID：${row.jumpValue || '-'}`
      }
      if (Number(row.jumpType) === 2) {
        const category = this.categoryOptions.find(item => String(item.id) === String(row.jumpValue))
        return category ? `${category.name}（ID: ${row.jumpValue}）` : `分类ID：${row.jumpValue || '-'}`
      }
      return '无跳转'
    },
    async reloadAll() {
      await Promise.allSettled([this.loadCategories(), this.searchProducts(''), this.loadData()])
    },
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        this.list = await getBannerList()
        await this.ensureProductOptionsByRows(this.list)
      } catch (error) {
        this.loadError = true
        this.$message.error(error?.message || '轮播图加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      try {
        const categories = await getCategoryList()
        this.categoryOptions = categories || []
      } catch (error) {
        this.categoryOptions = []
        this.$message.error(error?.message || '分类加载失败')
      }
    },
    async searchProducts(keyword) {
      this.productLoading = true
      try {
        const productPage = await getProductPage({
          pageNum: 1,
          pageSize: 100,
          name: keyword || undefined
        })
        this.productOptions = this.mergeProductOptions((productPage && productPage.list) || [])
      } catch (error) {
        this.productOptions = []
        this.$message.error(error?.message || '商品加载失败')
      } finally {
        this.productLoading = false
      }
    },
    openCreate() {
      this.form = createForm()
      this.dialogVisible = true
    },
    async openEdit(id) {
      this.dialogVisible = true
      try {
        const detail = await getBannerDetail(id)
        if (detail.jumpValue && Number(detail.jumpType) === 1) {
          await this.ensureProductOption(detail.jumpValue)
        }
        this.form = {
          id: detail.id,
          title: detail.title || '',
          subTitle: detail.subTitle || '',
          imageUrl: detail.imageUrl || '',
          jumpType: Number(detail.jumpType || 3),
          jumpValue: detail.jumpValue ? String(detail.jumpValue) : '',
          sort: Number(detail.sort || 0),
          status: Number(detail.status || 0)
        }
      } catch (error) {
        this.dialogVisible = false
        this.$message.error(error?.message || '轮播图详情加载失败')
      }
    },
    mergeProductOptions(list = []) {
      const merged = [...list]
      Object.values(this.productCache).forEach(item => {
        if (!merged.find(current => String(current.id) === String(item.id))) {
          merged.push(item)
        }
      })
      return merged
    },
    async ensureProductOption(productId) {
      const id = String(productId || '')
      if (!id) return
      if (this.productOptions.find(item => String(item.id) === id)) return
      if (this.productCache[id]) {
        this.productOptions = this.mergeProductOptions(this.productOptions)
        return
      }
      try {
        const detail = await getProductDetail(id)
        const option = { id: detail.id, name: detail.name }
        this.$set(this.productCache, id, option)
        this.productOptions = this.mergeProductOptions(this.productOptions)
      } catch (error) {
        // ignore single product lookup failure to avoid blocking the page
      }
    },
    async ensureProductOptionsByRows(rows = []) {
      const ids = rows
        .filter(item => Number(item.jumpType) === 1 && item.jumpValue)
        .map(item => String(item.jumpValue))
        .filter((item, index, arr) => arr.indexOf(item) === index)
      for (const id of ids) {
        await this.ensureProductOption(id)
      }
    },
    handleJumpTypeChange() {
      this.form.jumpValue = ''
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate('jumpValue')
      }
    },
    resetForm() {
      this.form = createForm()
      this.uploading = false
      this.saving = false
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate()
      }
    },
    async uploadImage(options) {
      try {
        this.uploading = true
        const result = await uploadAdminImage(options.file)
        this.form.imageUrl = result.url
      } catch (error) {
        this.$message.error(error?.message || '图片上传失败')
      } finally {
        this.uploading = false
      }
    },
    async submit() {
      try {
        if (Number(this.form.jumpType) === 3) {
          this.form.jumpValue = ''
        }
        await this.$refs.formRef.validate()
      } catch (error) {
        return
      }
      const payload = { ...this.form }
      this.saving = true
      try {
        if (payload.id) {
          await updateBanner(payload)
          this.$message.success('轮播图已更新')
        } else {
          await addBanner(payload)
          this.$message.success('轮播图已创建')
        }
        this.dialogVisible = false
        this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '轮播图保存失败')
      } finally {
        this.saving = false
      }
    },
    async up(row) {
      this.actionId = row.id
      try {
        await upBanner(row.id)
        this.$message.success('轮播图已启用')
        this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '轮播图启用失败')
      } finally {
        this.actionId = null
      }
    },
    async down(row) {
      this.actionId = row.id
      try {
        await downBanner(row.id)
        this.$message.success('轮播图已停用')
        this.loadData()
      } catch (error) {
        this.$message.error(error?.message || '轮播图停用失败')
      } finally {
        this.actionId = null
      }
    },
    remove(row) {
      this.$confirm(`确认删除轮播图“${row.title}”吗？`, '删除轮播图', { type: 'warning' })
        .then(async () => {
          this.actionId = row.id
          try {
            await deleteBanner(row.id)
            this.$message.success('轮播图已删除')
            this.loadData()
          } catch (error) {
            this.$message.error(error?.message || '轮播图删除失败')
          } finally {
            this.actionId = null
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.state-block {
  margin-bottom: 16px;
}

.banner-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 64px;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f6f7;
  border: 1px solid #ebeef5;
}

.banner-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-thumb__empty {
  color: #a0a6ad;
  font-size: 12px;
}

.banner-thumb--large {
  width: 180px;
  height: 112px;
}

.banner-title {
  color: #303133;
  font-weight: 600;
}

.sub-text {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.upload-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.upload-panel {
  flex: 1;
}

.upload-inline {
  margin-top: 8px;
}
</style>
